/**
 * QAVIA behaviours. Small, dependency-free, and safe to run more than once
 * (the theme editor re-renders sections).
 */

const root = document.documentElement;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

root.classList.add('q-js');
// Tells the inline guard in snippets/qavia-brand.liquid that reveal styles can stay on
window.qaviaReady = true;

/* ---------- Scroll reveal ---------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

/**
 * @param {ParentNode} scope
 */
function initReveal(scope) {
  for (const el of scope.querySelectorAll('[data-q-reveal]:not(.is-in)')) {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-in');
  }
}

/* ---------- Hero wall: tilt with the pointer, swap tiles now and then ---------- */

/** @type {WeakMap<Element, AbortController>} */
const heroControllers = new WeakMap();

/**
 * @param {HTMLElement} hero
 */
function initHero(hero) {
  heroControllers.get(hero)?.abort();
  const controller = new AbortController();
  heroControllers.set(hero, controller);
  const { signal } = controller;

  requestAnimationFrame(() => hero.classList.add('is-ready'));

  const wall = hero.querySelector('.q-wall');
  if (!(wall instanceof HTMLElement) || reducedMotion.matches) return;

  if (finePointer.matches) {
    let frame = 0;
    hero.addEventListener(
      'pointermove',
      (event) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const rect = hero.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
          wall.style.setProperty('--q-px', x.toFixed(3));
          wall.style.setProperty('--q-py', y.toFixed(3));
        });
      },
      { signal, passive: true }
    );
    hero.addEventListener(
      'pointerleave',
      () => {
        wall.style.setProperty('--q-px', '0');
        wall.style.setProperty('--q-py', '0');
      },
      { signal }
    );
  }

  let visible = true;
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(([entry]) => (visible = Boolean(entry?.isIntersecting)));
    io.observe(hero);
    signal.addEventListener('abort', () => io.disconnect());
  }

  const swap = () => {
    if (!visible || document.hidden) return;
    const cells = /** @type {HTMLElement[]} */ (
      [...wall.querySelectorAll('[data-q-swap]')].filter((cell) => cell instanceof HTMLElement && cell.offsetParent !== null)
    );
    if (cells.length < 2) return;
    const a = cells[Math.floor(Math.random() * cells.length)];
    let b = cells[Math.floor(Math.random() * cells.length)];
    if (a === b) b = cells[(cells.indexOf(a) + 1) % cells.length];
    if (!a || !b) return;
    const imgA = a.querySelector('img');
    const imgB = b.querySelector('img');
    if (!imgA || !imgB) return;

    a.classList.add('is-swapping');
    b.classList.add('is-swapping');
    window.setTimeout(() => {
      const srcA = imgA.getAttribute('src');
      const colourA = [...a.classList].find((name) => /^q-wall__cell--(olive|orange|cream|sand|caramel)$/.test(name));
      const colourB = [...b.classList].find((name) => /^q-wall__cell--(olive|orange|cream|sand|caramel)$/.test(name));
      imgA.setAttribute('src', imgB.getAttribute('src') ?? '');
      imgB.setAttribute('src', srcA ?? '');
      if (colourA && colourB && colourA !== colourB) {
        a.classList.replace(colourA, colourB);
        b.classList.replace(colourB, colourA);
      }
      a.classList.remove('is-swapping');
      b.classList.remove('is-swapping');
    }, 320);
  };

  const timer = window.setInterval(swap, 2600);
  signal.addEventListener('abort', () => window.clearInterval(timer));
}

/* ---------- Category explorer: hovering or focusing a row shows its panel ---------- */

/**
 * @param {HTMLElement} explorer
 */
function initExplorer(explorer) {
  if (explorer.dataset.qBound === 'true') return;
  explorer.dataset.qBound = 'true';

  const rows = /** @type {HTMLElement[]} */ ([...explorer.querySelectorAll('[data-q-explorer-row]')]);
  const panels = /** @type {HTMLElement[]} */ ([...explorer.querySelectorAll('[data-q-explorer-panel]')]);

  /** @param {number} index */
  const activate = (index) => {
    rows.forEach((row, i) => row.classList.toggle('is-active', i === index));
    panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
  };

  rows.forEach((row, index) => {
    row.addEventListener('pointerenter', () => activate(index));
    row.addEventListener('focusin', () => activate(index));
  });
}

/* ---------- Tabs (solutions) ---------- */

/**
 * @param {HTMLElement} tabs
 */
function initTabs(tabs) {
  if (tabs.dataset.qBound === 'true') return;
  tabs.dataset.qBound = 'true';

  const buttons = /** @type {HTMLElement[]} */ ([...tabs.querySelectorAll('[role="tab"]')]);
  const panels = /** @type {HTMLElement[]} */ ([...tabs.querySelectorAll('[role="tabpanel"]')]);

  /**
   * @param {number} index
   * @param {boolean} [focus]
   */
  const select = (index, focus = false) => {
    buttons.forEach((button, i) => {
      const selected = i === index;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
      if (selected && focus) button.focus();
    });
    panels.forEach((panel, i) => {
      panel.classList.toggle('is-hidden-js', i !== index);
    });
  };

  buttons.forEach((button, index) => {
    button.addEventListener('click', () => select(index));
    button.addEventListener('keydown', (event) => {
      const rtl = getComputedStyle(tabs).direction === 'rtl';
      const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
      const back = rtl ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === forward) select((index + 1) % buttons.length, true);
      else if (event.key === back) select((index - 1 + buttons.length) % buttons.length, true);
      else if (event.key === 'Home') select(0, true);
      else if (event.key === 'End') select(buttons.length - 1, true);
      else return;
      event.preventDefault();
    });
  });
}

/* ---------- Quote / enquiry dialog ---------- */

/**
 * Buttons with [data-q-open-dialog="id"] open the matching <dialog>. A product name on the
 * button is copied into the form so the enquiry always says which product it is about.
 * @param {Document | HTMLElement} scope
 */
function initDialogs(scope) {
  /**
   * Adds the options chosen in the variant picker (size, colour…) to the product name, so the
   * enquiry says exactly which version the customer wants.
   * @param {HTMLElement} trigger
   * @param {string} name
   */
  const withChosenOptions = (trigger, name) => {
    const root = trigger.closest('.shopify-section') ?? document;
    const chosen = [];
    for (const field of root.querySelectorAll('variant-picker input[type="radio"]:checked, variant-picker select')) {
      if ((field instanceof HTMLInputElement || field instanceof HTMLSelectElement) && field.value) chosen.push(field.value);
    }
    return chosen.length ? `${name} (${chosen.join(' / ')})`.slice(0, 250) : name;
  };

  for (const trigger of scope.querySelectorAll('[data-q-open-dialog]')) {
    if (!(trigger instanceof HTMLElement) || trigger.dataset.qBound === 'true') continue;
    trigger.dataset.qBound = 'true';
    trigger.addEventListener('click', (event) => {
      const dialog = document.getElementById(trigger.dataset.qOpenDialog ?? '');
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      const productField = dialog.querySelector('[data-q-product-field]');
      if (productField instanceof HTMLInputElement && trigger.dataset.qProduct) {
        productField.value = withChosenOptions(trigger, trigger.dataset.qProduct);
      }
      dialog.showModal();
    });
  }

  for (const dialog of scope.querySelectorAll('dialog.q-dialog')) {
    if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.qBound === 'true') continue;
    dialog.dataset.qBound = 'true';
    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (target === dialog || (target instanceof Element && target.closest('[data-q-close-dialog]'))) dialog.close();
    });
    // After the form inside was submitted the page reloads: reopen so the result is seen
    if (dialog.querySelector('[data-q-form-status]') && !dialog.open) dialog.showModal();
  }

  // Move focus to a form result outside dialogs so it is announced and visible
  for (const status of scope.querySelectorAll('[data-q-form-status]')) {
    if (status instanceof HTMLElement && !status.closest('dialog')) {
      status.scrollIntoView({ block: 'center' });
      status.focus({ preventScroll: true });
      break;
    }
  }
}

/* ---------- Forms: carry ?product= into the product field ---------- */

function initFormPrefill() {
  const params = new URLSearchParams(window.location.search);
  const product = params.get('product');
  if (!product) return;
  for (const field of document.querySelectorAll('[data-q-product-field]')) {
    if (field instanceof HTMLInputElement && !field.value) field.value = product.slice(0, 200);
  }
}

/* ---------- Boot ---------- */

/**
 * @param {Document | HTMLElement} scope
 */
function init(scope) {
  initReveal(scope);
  for (const hero of scope.querySelectorAll('[data-q-hero]')) if (hero instanceof HTMLElement) initHero(hero);
  for (const explorer of scope.querySelectorAll('[data-q-explorer]')) if (explorer instanceof HTMLElement) initExplorer(explorer);
  for (const tabs of scope.querySelectorAll('[data-q-tabs]')) if (tabs instanceof HTMLElement) initTabs(tabs);
  initDialogs(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    initFormPrefill();
  });
} else {
  init(document);
  initFormPrefill();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
});
